import { useEffect, useRef, useState } from "react";
import type { ScannerEvent, ScannerStatus, ScannerItem } from "./api";

type HitMap = Record<string, ScannerItem>;

interface Progress {
  scanned: number;
  total: number;
  queueDepth: number;
  current: string;
  lastTickAt: string;
  circuitOpen: boolean;
}

interface UseScannerResult {
  /** Latest status frame the server sent. Null before connect. */
  status: ScannerStatus | null;
  /** Live progress state — bumped on every scan tick. */
  progress: Progress | null;
  /** Newly-found setups, keyed by ticker. Survives across SSE frames
   *  so the UI can keep a rolling "found so far this session" list. */
  hits: HitMap;
  /** Most recent error message the server published (status events
   *  or dedicated error frames). */
  lastError: string | null;
  /** True while the EventSource is attempting to connect. */
  connecting: boolean;
}

/**
 * Subscribes to `/api/scanner/stream` and exposes the latest status +
 * progress + accumulated hits. Mirrors the shape of useTracker but the
 * scanner emits four distinct event types that we dispatch on
 * `evt.type`.
 *
 * `enabled=false` tears down the EventSource. The scanner stream is
 * cheap to hold open (one frame per ~2 s scan tick plus a 15 s
 * heartbeat), so we leave it active for the entire session once the
 * user opens the panel.
 */
export function useScanner(enabled: boolean): UseScannerResult {
  const [status, setStatus] = useState<ScannerStatus | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [hits, setHits] = useState<HitMap>({});
  const [lastError, setLastError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      return;
    }

    setConnecting(true);
    const es = new EventSource("/api/scanner/stream");
    esRef.current = es;

    es.onopen = () => setConnecting(false);
    es.onmessage = (ev) => {
      try {
        const evt: ScannerEvent = JSON.parse(ev.data);
        switch (evt.type) {
          case "status":
            setStatus(evt.status);
            if (evt.status.last_error) setLastError(evt.status.last_error);
            break;
          case "progress":
            setProgress({
              scanned: evt.progress.scanned,
              total: evt.progress.total,
              queueDepth: evt.progress.queue_depth,
              current: evt.progress.current ?? "",
              lastTickAt: evt.progress.last_tick_at,
              circuitOpen: evt.progress.circuit_open,
            });
            break;
          case "hit":
            setHits((prev) => ({ ...prev, [evt.hit.ticker]: evt.hit }));
            break;
          case "error":
            setLastError(evt.message);
            break;
        }
      } catch (err) {
        console.warn("scanner: dropped bad frame", err);
      }
    };
    es.onerror = () => {
      setConnecting(true);
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [enabled]);

  return { status, progress, hits, lastError, connecting };
}
